import { useState } from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import {
  Alert,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { AnimatePresence } from 'framer-motion';
import { HiX } from 'react-icons/hi';
import { OverlayTriggerState } from 'react-stately';
import {
  useActiveTenant,
  useTenants,
  useSetCurrentTenant,
} from '~hooks/useTenant';
import Modal from '~components/common/Modal';
import { Icon, Spacer, Stack, Text } from '~uikit';

type Props = {
  state: OverlayTriggerState;
};

export default function SwitchTenantModal({ state }: Props) {
  const { data: tenant } = useActiveTenant();
  const { data: tenants } = useTenants();
  const setCurrentTenantMutation = useSetCurrentTenant();
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);

  const selectTenant = async (value: string) => {
    const newTenant = tenants
      ? tenants.data.find(t => t.tenantId === value || t.displayName === value)
      : null;
    if (newTenant) {
      setCurrentTenantMutation.reset();
      setSwitching(true);
      setSwitchError(null);
      try {
        // 1. Switch tenant on the backend
        await setCurrentTenantMutation.mutateAsync({
          tenantId: newTenant.tenantId,
        });
        // 2. Hard reload — the cleanest way to ensure all state (React Query
        //    caches, Contexts, SignalR connections, module-level singletons)
        //    is fully reset for the new tenant.  Keycloak re-inits with
        //    login-required and gets a fresh token automatically.
        window.location.href = '/admin';
      } catch {
        setSwitching(false);
        setSwitchError(t`Failed to switch tenant. Please try again.`);
      }
    }
  };

  return (
    <AnimatePresence>
      {state.isOpen && (
        <Modal
          onClose={() => {
            if (!switching) state.close();
          }}
          title={t`Switch tenant`}
        >
          <Modal.Header>
            <Stack
              axis="x"
              spacing="xsmall"
              justify="space-between"
              align="center"
            >
              <Text variant="title3">
                <Trans>Switch tenant</Trans>
              </Text>
              <IconButton
                size="medium"
                disabled={switching}
                onClick={() => {
                  if (!switching) state.close();
                }}
              >
                <Icon
                  icon={HiX}
                  size="large"
                  color="text"
                  aria-hidden="true"
                  className="breadcrumb-separator"
                />
              </IconButton>
            </Stack>
          </Modal.Header>
          <Modal.Content>
            <Stack
              axis="y"
              spacing="normal"
              align="center"
              style={{ minWidth: '600px', minHeight: '200px' }}
            >
              <Text variant="bodySmallStrong">
                <Trans>Tenant</Trans>
              </Text>
              {tenant && tenants && (
                <FormControl variant="outlined" size="small">
                  <InputLabel id="filter-1-label">{t`Tenant`}</InputLabel>
                  <Select
                    variant="outlined"
                    disabled={switching}
                    value={tenant.displayName || tenant.tenantId}
                    onChange={e => {
                      selectTenant(e.target.value);
                    }}
                    label={t`Tenant`}
                    labelId="filter-1-label"
                  >
                    {tenants.data.map(t => (
                      <MenuItem
                        key={t.tenantId}
                        value={t.displayName || t.tenantId}
                      >
                        {t.displayName || t.tenantId}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <Spacer axis="y" size="medium" />
              {switchError && (
                <Alert severity="error" sx={{ width: '100%' }}>
                  {switchError}
                </Alert>
              )}
              {switching ? (
                <Stack axis="y" spacing="xsmall" align="center">
                  <CircularProgress size={24} />
                  <Text variant="caption">
                    <Trans>Switching tenant...</Trans>
                  </Text>
                </Stack>
              ) : (
                <Stack axis="y" spacing="xsmall" align="center">
                  <Text variant="caption">
                    <Trans>
                      Can&apos;t see the tenant you are looking for?
                    </Trans>
                  </Text>
                  <Text variant="caption">
                    <Trans>Send email to support@cozify.fi</Trans>
                  </Text>
                </Stack>
              )}
            </Stack>
          </Modal.Content>
        </Modal>
      )}
    </AnimatePresence>
  );
}
